// Vitesse et estimations affichées à l'opérateur. Une absence de mesure n'est jamais un zéro; une distance à vol
// d'oiseau ne donne jamais d'heure d'arrivée; une durée du profil automobile n'est pas celle d'un tracteur.

/** Au-delà, la vitesse n'est plus affichée (« — »). */
export const SPEED_MAX_AGE_MS = 5_000
/** Lissage exponentiel : évite les oscillations d'un relevé à l'autre. */
export const SPEED_SMOOTHING = 0.35
/** Sous ce seuil (km/h), la vitesse affichée est 0 : immobile. */
export const SPEED_STILL_KMH = 1.5

export interface SpeedState {
  kmh: number | null
  at: number | null
}

export const initialSpeed: SpeedState = { kmh: null, at: null }

/** Vitesse GPS (m/s) d'un relevé; `null` si le relevé n'en donne pas. */
export function filterSpeed(state: SpeedState, speedMps: number | null, at: number): SpeedState {
  if (speedMps === null || !Number.isFinite(speedMps) || speedMps < 0) return state
  const kmh = speedMps * 3.6
  const fresh = state.kmh !== null && state.at !== null && at - state.at <= SPEED_MAX_AGE_MS
  return { kmh: fresh && state.kmh !== null ? state.kmh + (kmh - state.kmh) * SPEED_SMOOTHING : kmh, at }
}

/** Libellé de vitesse : « — » sans mesure récente. */
export function speedLabel(state: SpeedState, now: number): string {
  if (state.kmh === null || state.at === null || now - state.at > SPEED_MAX_AGE_MS) return '—'
  return state.kmh < SPEED_STILL_KMH ? '0' : String(Math.round(state.kmh))
}

export type TravelEstimate =
  /** Distance par le parcours prévu, durée du profil automobile du fournisseur. */
  | { kind: 'ROUTE'; meters: number; seconds: number }
  /** Voisine : la durée n'apprend rien. */
  | { kind: 'NEAR'; meters: number }
  /** Seule une distance directe est connue : jamais d'heure d'arrivée. */
  | { kind: 'DIRECT'; meters: number }

/** En deçà, « À proximité » plutôt qu'un « 1 min » systématique. */
export const NEAR_METERS = 60

export function travelEstimate(input: {
  routeMeters: number | null
  routeSeconds: number | null
  directMeters: number | null
}): TravelEstimate | null {
  if (input.routeMeters !== null && input.routeSeconds !== null) {
    if (input.routeMeters < NEAR_METERS) return { kind: 'NEAR', meters: input.routeMeters }
    return { kind: 'ROUTE', meters: input.routeMeters, seconds: input.routeSeconds }
  }
  if (input.directMeters !== null) {
    if (input.directMeters < NEAR_METERS) return { kind: 'NEAR', meters: input.directMeters }
    return { kind: 'DIRECT', meters: input.directMeters }
  }
  return null
}

/** Arrondi cohérent : 10 m sous 1 km, sinon 0,1 km. */
export function distanceLabel(meters: number): string {
  if (meters < 1000) return `${String(Math.max(10, Math.round(meters / 10) * 10))} m`
  return `${(meters / 1000).toFixed(1).replace('.', ',')} km`
}

function arrival(now: Date, seconds: number): string {
  const at = new Date(now.getTime() + seconds * 1000)
  return `${String(at.getHours())} h ${String(at.getMinutes()).padStart(2, '0')}`
}

/** Ligne compacte de la fiche cible (« 300 m · 1 min · Arrivée vers 13 h 29 »). */
export function estimateLabel(estimate: TravelEstimate, now: Date): string {
  if (estimate.kind === 'NEAR') return `À proximité · ${distanceLabel(estimate.meters)}`
  if (estimate.kind === 'DIRECT') return `${distanceLabel(estimate.meters)} à vol d’oiseau (approximatif)`
  const minutes = Math.max(1, Math.round(estimate.seconds / 60))
  return `${distanceLabel(estimate.meters)} · ${String(minutes)} min · Arrivée vers ${arrival(now, estimate.seconds)}`
}
